import type { BadgeVariant } from '../components/common/Badge';

/**
 * Single source of truth for status → Badge variant mapping.
 * Used by every card, table, and modal across the app.
 *
 * Rule: lifecycle stages map to a consistent semantic color:
 *   Not started / Draft / Open   → warning  (amber  — action needed)
 *   In Progress / Submitted      → info     (blue   — in flight)
 *   Complete / Done / Closed     → success  (green  — finished)
 *   Overdue / Rejected / Failed  → danger   (red    — problem)
 *   Archived / Cancelled         → neutral  (gray   — inactive)
 */
export function statusVariant(status?: string | null): BadgeVariant {
    switch (status?.toLowerCase().replace(/[_\s-]+/g, '-')) {
        // ── Terminal success ──────────────────────────────────────
        case 'complete':
        case 'completed':
        case 'done':
        case 'verified':
        case 'closed':
        case 'resolved':
        case 'approved':
        case 'pass':
        case 'matched':
        case 'complied':
            return 'success';

        // ── Active / live ─────────────────────────────────────────
        case 'active':
            return 'success';

        // ── In flight ─────────────────────────────────────────────
        case 'in-progress':
        case 'submitted':
        case 'fulfilling':
        case 'under-review':
        case 'review':
        case 'scheduled':
            return 'info';

        // ── Needs attention / not yet started ─────────────────────
        case 'open':
        case 'not-started':
        case 'draft':
        case 'pending':
        case 'pending-approval':
        case 'reverted':
        case 'needsmapping':
        case 'paused':
            return 'warning';

        // ── Problem states ────────────────────────────────────────
        case 'overdue':
        case 'rejected':
        case 'failed':
        case 'fail':
        case 'not-complied':
        case 'notcomplied':
        case 'variance':
        case 'at-risk':
        case 'retired':
            return 'danger';

        // ── Inactive / inert ─────────────────────────────────────
        case 'inactive':
        case 'archived':
        case 'cancelled':
        case 'canceled':
        case 'na':
            return 'neutral';

        default:
            return 'neutral';
    }
}

/** Maps BadgeVariant → status-badge CSS string (for components not using <Badge>) */
export function statusBadgeCss(status?: string | null, size: 'sm' | 'xs' = 'sm'): string {
    return `status-badge ${size} ${statusVariant(status)}`;
}

/**
 * RAT-stage badge — the canonical maker/checker lifecycle styling used by the
 * GRC "RAT Programs" screen (RiskPrograms.tsx). Staff worklists (ITAT, Control
 * Testing) reuse this so every worklist reads with identical stage labels AND
 * colours: Not-Started (amber) → In-Progress (blue) → Submitted (purple) →
 * Closed (green).
 *
 * Backends that still emit the older Complete/Approved vocabulary are remapped
 * here for display only (Complete→Submitted, Approved→Closed).
 */
const RAT_STAGE_LABEL: Record<string, string> = {
    Complete: 'Submitted',
    Approved: 'Closed',
};

/** Remap a raw backend status to the RAT stage label shown to the user. */
export function ratStageLabel(status?: string | null): string {
    const s = status || 'Not-Started';
    return RAT_STAGE_LABEL[s] || s;
}

/** Tailwind pill classes for a RAT stage (matches RiskPrograms.tsx exactly). */
export function ratStageBadgeClass(status?: string | null): string {
    switch (ratStageLabel(status)) {
        case 'Closed':      return 'bg-green-100 text-green-700';
        case 'Submitted':   return 'bg-purple-100 text-purple-700';
        case 'In-Progress': return 'bg-blue-100 text-blue-700';
        case 'Not-Started': return 'bg-amber-100 text-amber-700';
        default:            return 'bg-gray-100 text-gray-600';
    }
}

/**
 * Severity / priority → Badge variant mapping.
 *   Critical / High  → danger  (red)
 *   Medium           → warning (amber)
 *   Low / Minimal    → info    (blue)
 *   None / Unknown   → neutral (gray)
 */
export function severityVariant(severity?: string | null): BadgeVariant {
    switch (severity?.toLowerCase().replace(/[_\s-]+/g, '-')) {
        case 'critical':
        case 'high':
            return 'danger';
        case 'medium':
        case 'moderate':
            return 'warning';
        case 'low':
        case 'minimal':
            return 'info';
        case 'none':
            return 'neutral';
        default:
            return 'neutral';
    }
}

/** Maps severity → status-badge CSS string */
export function severityBadgeCss(severity?: string | null, size: 'sm' | 'xs' = 'sm'): string {
    return `status-badge ${size} ${severityVariant(severity)}`;
}

/**
 * is_active integer (1 = active, 0/−1 = inactive) → Badge variant.
 *   1  → success (green)
 *   else → neutral (gray)
 */
export function activeVariant(isActive?: number | boolean | null): BadgeVariant {
    return isActive === 1 || isActive === true ? 'success' : 'neutral';
}

/** Maps is_active → status-badge CSS string */
export function activeBadgeCss(isActive?: number | boolean | null, size: 'sm' | 'xs' = 'sm'): string {
    return `status-badge ${size} ${activeVariant(isActive)}`;
}
